import { useMemo, useState } from "react"
import { useTranslation } from "react-i18next"
import { ConnectionHandler } from "react-relay"
import { Plus } from "@phosphor-icons/react"
import { format, startOfDay } from "date-fns"
import { ScrollWrapper } from "@/components/scroll-wrapper"
import { Button } from "@/components/ui/button"
import { toast } from "@/hooks/useToast"
import { Header } from "@/layouts/header"
import { NewsFormDialog } from "@/modules/admin/news/components/news-form-dialog"
import { NewsGrid } from "@/modules/admin/news/components/news-grid"
import { NewsItem } from "@/modules/admin/news/components/news-item"
import { PLANNING_NEWS, SHOWING_NEWS } from "@/modules/admin/news/constants"
import { combineDateAndTime } from "@/modules/admin/news/helpers/news-helper"
import { NewsFragment$data } from "@/modules/admin/news/services/__generated__/NewsFragment.graphql"
import { useDeleteNewsMutation } from "@/modules/admin/news/services/useDeleteNewsMutation"
import useNewsQuery, { NewsConnectionKey } from "@/modules/admin/news/services/useNewsQuery"
import { useUpdateNewsNotificationMutation } from "@/modules/admin/news/services/useUpdateNewsMutation"
import { NewsFormType } from "@/modules/admin/news/types"
import { useConfirmStore } from "@/stores/useConfirmStore"

const News = () => {
  const { t } = useTranslation()
  const [data] = useNewsQuery()
  const [isDialogOpen, setIsDialogOpen] = useState(false)
  const [formState, setFormState] = useState<NewsFormType | null>(null)
  const [newData, setNewData] = useState<NewsFragment$data | null>(null)
  const confirm = useConfirmStore((state) => state.confirm)
  const cancel = useConfirmStore((state) => state.cancel)

  const newsNodes = data.news?.edges?.map((edge) => edge.node)
  const showingNews = useMemo(() => newsNodes?.filter((newsItem) => newsItem.isShowing), [newsNodes])
  const plannedNews = useMemo(() => newsNodes?.filter((newsItem) => !newsItem.isShowing), [newsNodes])
  const [updateNews] = useUpdateNewsNotificationMutation()
  const [deleteNews] = useDeleteNewsMutation()

  const handleOpenAddNewsForm = () => {
    setFormState("CREATE")
    setIsDialogOpen(true)
    setNewData(null)
  }

  const handleOpenEditNewsForm = (data: NewsFragment$data) => {
    setNewData(data)
    setFormState("EDIT")
    setIsDialogOpen(true)
  }

  const handleShowingNews = (data: NewsFragment$data) => {
    confirm({
      title: t("common.confirmation.saveDisabledNewChatTitle"),
      content: "",
      cancelText: t("common.button.cancel"),
      confirmText: t("common.confirmation.save"),
      onConfirm: () => {
        const payloadShowingNews = {
          criticalityLevel: data.criticalityLevel,
          englishContent: data.englishContent ?? "",
          germanContent: data.germanContent ?? "",
          frenchContent: data.frenchContent,
          italianContent: data.italianContent,
          displayFor: data.displayFor,
          startTime: combineDateAndTime(startOfDay(new Date()), format(new Date(), "HH:mm")),
          endTime: data.endTime,
        }
        updateNews({
          variables: {
            input: {
              id: data?.id ?? "",
              criticalityLevel: payloadShowingNews.criticalityLevel,
              englishContent: payloadShowingNews.englishContent,
              germanContent: payloadShowingNews.germanContent,
              frenchContent: payloadShowingNews.frenchContent,
              italianContent: payloadShowingNews.italianContent,
              displayFor: payloadShowingNews.displayFor,
              startTime: payloadShowingNews.startTime,
              endTime: payloadShowingNews.endTime,
            },
          },
          onCompleted: (response) => {
            const errMessage = response.updateNews?.errors?.[0]?.message
            if (errMessage) {
              toast({
                description: errMessage,
              })
            } else {
              toast({
                description: t("admin.news.showNow"),
              })
            }
            cancel()
          },
          onError: (error: Error) => {
            console.error(error)
          },
        })
      },
    })
  }

  const handleDeleteNews = (id: string) => {
    const connectionToAdd = ConnectionHandler.getConnectionID("client:root", NewsConnectionKey)
    confirm({
      title: t("admin.news.message.deleteTitle"),
      content: "",
      cancelText: t("common.button.cancel"),
      confirmText: t("common.button.delete"),
      onConfirm: () => {
        deleteNews({
          variables: { deleteNews: { id: id }, connections: [connectionToAdd] },
          onCompleted: (response) => {
            const errMessage = response.deleteNews?.errors?.[0]?.message
            if (errMessage) {
              toast({
                description: errMessage,
              })
            } else {
              toast({
                description: t("admin.news.message.deleteSuccess"),
              })
            }
            cancel()
          },
          onError: (error: Error) => {
            console.error(error)
          },
        })
      },
    })
  }

  return (
    <div className="overflow-auto">
      <Header title={t("admin.news.pageTitle")}>
        <Button className="pl-4" onClick={handleOpenAddNewsForm}>
          <Plus size={24} />
          {t("admin.news.plannedNews")}
        </Button>
      </Header>

      {newsNodes && newsNodes.length > 0 ? (
        <>
          <div className="grid-cols-1fr-auto bg-background-subdued grid grid-rows-1 border-b-2">
            {showingNews && showingNews.length > 0 && (
              <ScrollWrapper className="scrollbar-w-1 hover:scrollbar-thumb-surface-pressed scrollbar-track-transparent p-6">
                <div className="mx-auto flex w-full flex-col gap-2 2xl:max-w-384">
                  <h2 className="text-on-surface text-base font-medium" data-testid="news-title">
                    {t("admin.news.showingNews")}
                  </h2>
                  <NewsGrid className="text-on-surface-subdued grid items-center gap-4 px-4 pt-2 text-xs leading-4 font-normal tracking-[.25px]">
                    <span>{t("admin.news.criticalityLevelTitle")}</span>
                    <span className="truncate">{t("admin.news.content")}</span>
                    <span>{t("admin.news.duration")}</span>
                    <span>{t("admin.news.displayFor")}</span>
                    <span></span>
                  </NewsGrid>
                  {showingNews.map((news) => (
                    <NewsItem
                      key={news.id}
                      news={news}
                      onOpenEditNew={handleOpenEditNewsForm}
                      onShow={handleShowingNews}
                      onDelete={handleDeleteNews}
                      onStop={handleDeleteNews}
                      type={SHOWING_NEWS}
                    />
                  ))}
                </div>
              </ScrollWrapper>
            )}
          </div>

          <div className="grid-cols-1fr-auto bg-background-subdued grid grid-rows-1" data-testid="news-page">
            {plannedNews && plannedNews.length > 0 && (
              <ScrollWrapper className="scrollbar-w-1 hover:scrollbar-thumb-surface-pressed scrollbar-track-transparent p-6">
                <div className="mx-auto flex w-full flex-col gap-2 2xl:max-w-384">
                  <h2 className="text-on-surface text-base font-medium" data-testid="news-title">
                    {t("admin.news.plannedNews")}
                  </h2>
                  <NewsGrid className="text-on-surface-subdued grid items-center gap-4 px-4 pt-2 text-xs leading-4 font-normal tracking-[.25px]">
                    <span>{t("admin.news.criticalityLevelTitle")}</span>
                    <span className="truncate">{t("admin.news.content")}</span>
                    <span>{t("admin.news.duration")}</span>
                    <span>{t("admin.news.displayFor")}</span>
                    <span></span>
                  </NewsGrid>
                  {plannedNews.map((news) => (
                    <NewsItem
                      key={news.id}
                      news={news}
                      onOpenEditNew={handleOpenEditNewsForm}
                      onShow={handleShowingNews}
                      onDelete={handleDeleteNews}
                      onStop={handleDeleteNews}
                      type={PLANNING_NEWS}
                    />
                  ))}
                </div>
              </ScrollWrapper>
            )}
          </div>
        </>
      ) : (
        <div className="animate-fade-in-bottom content-center items-center justify-center gap-4 p-3 text-center">
          <div className="grid gap-1">
            <h2 className="text-on-surface text-xl font-bold">{t("common.dataNotFound")}</h2>
          </div>
        </div>
      )}

      {isDialogOpen && (
        <NewsFormDialog typeForm={formState} newsData={newData} onClose={() => setIsDialogOpen(false)} />
      )}
    </div>
  )
}

export { News }
